export const openAi = {
  aiModel: '',
  aiTokens: '4096',
  aiFp: '0.5',
  aiPp: '0.5',
  aiTemperature: '0.5',
};

export enum OpenAIModels {
  GPT5_2 = 'gpt-5.2',
  GPT5_1 = 'gpt-5.1',
  GPT5 = 'gpt-5',
  GPT5_MINI = 'gpt-5-mini',
  GPT5_NANO = 'gpt-5-nano',
  GPT4_1 = 'gpt-4.1',
  GPT4 = 'gpt-4',
  GPT3 = 'gpt-3.5-turbo',
  GPT_4_1106_PREVIEW = 'gpt-4-1106-preview',
  TEXT_DAVINCI_003 = 'text-davinci-003',
  TEXT_CURIE_001 = 'text-curie-001',
  TEXT_BABBAGE_001 = 'text-babbage-001',
  TEXT_ADA_001 = 'text-ada-001',
  GPT4_0613 = 'gpt-4-0613',
  GPT3_TURBO_0613 = 'gpt-3.5-turbo-0613',
  GPR3_TURBO_0613_16k = 'gpt-3.5-turbo-16k-0613',
  GPR3_TURBO_16K = 'gpt-3.5-turbo-16k',
  GPR3_TURBO_1106 = 'gpt-3.5-turbo-1106',
  GPT_35_TURBO_0125 = 'gpt-3.5-turbo-0125',
  GPT4_0125_PREVIEW = 'gpt-4-0125-preview',
  GPT_4_TURBO_PREVIEW = 'gpt-4-turbo-preview',
  GPT_3_5_TURBO_INSTRUCT = 'gpt-3.5-turbo-instruct',
  GPT_4O = `gpt-4o`,
  GPT_4_VISION_PREVIEW = 'gpt-4-vision-preview',
  GPT_4o_mini = 'gpt-4o-mini',
  O3_MINI_2025_01_31 = 'o3-mini-2025-01-31',
  O1 = 'o1',
}

export const voicePlayHt = {
  voice: 'Evelyn',
  speed: 1,
  quality: 'medium',
  outputFormat: 'mp3',
  sampleRate: 24000,
};

export const imageAi = {
  aiSystem: 'stabilityai',
  aiModel: 'sd3',
  cfg_scale: 8,
  height: 1024,
  width: 1024,
  steps: 50,
  clip_guidance_preset: 'NONE',
  type: 'Text to Image',
  promptWeight: 1.0,
  image_strength: 0.0,
  negativePrompt: '',
  style_present: 'None',
  sampler: 'AUTO',
  toneDefaultValues: [],
  viewDefaultValues: [],
  quality: 'high',
  numbersOfVariations: 1,
  size: '1024x1024',
};

export const groqAi = {
  seed: 50,
  maxTokens: 4000,
  aiTemperature: 0.5,
  topP: 0.5,
  groqStop: '',
};

export const geminiAi = {
  system: 'gemini',
  maxOutputTokens: 256,
  temperature: 0.5,
  topP: 0.5,
  topK: 40,
};

export const modelTokenLimits = [
  { model: 'gpt-4o', tokens: 16384 },
  { model: 'gpt-4o-mini', tokens: 16384 },
  { model: 'gpt-4-turbo-preview', tokens: 4096 },
  { model: 'gpt-4-1106-preview', tokens: 4096 },
  { model: 'gpt-4', tokens: 8192 },
  { model: 'gpt-4-0613', tokens: 8192 },
  { model: 'gpt-4-0125-preview', tokens: 4096 },
  { model: 'gpt-3.5-turbo', tokens: 4096 },
  { model: 'gpt-3.5-turbo-0125', tokens: 4096 },
  { model: 'gpt-3.5-turbo-1106', tokens: 4096 },
  { model: 'gpt-3.5-turbo-instruct', tokens: 4096 },
];

export type AiTextGeneratorFormValues = {
  /** Canonical provider key (lowercase). One of: openai | anthropic | gemini | groq | perplexity | xai | opensources */
  aiSystem: string;
  aiFp: string;
  /** Canonical model id (exact SDK id). Single source of truth for the selected model regardless of provider. */
  aiModel: string;
  aiPp: string;
  aiTemperature: string;
  aiTokens: string;
  aiEffort: 'low' | 'medium' | 'high';
  maxAiTokens: string;
  maxOutputTokens: number;
  temperature: number;
  topP: number;
  topK: number;
  type: string;
  modelName: string;
  text: string;
  max_length: number;
  top_p: number;
  repetition_penalty: number;
  length_penalty: number;
  no_repeat_ngram_size: number;
  seed: number;
  max_new_tokens: number;
  system_prompt: string;
  min_new_tokens: number;
  prompt: string;
  content: string;
  instruction: string;
  persona: string;
  autoToken: boolean;
  max_tokens: number;
  groqTemperature: number;
  groqMaxTokens: number;
  groqTopP: number;
  groqStop: string;
  groqSeed: number;
  groqName: string;
  stop?: string;
  geminiTemp: number;
  geminiTopP: number;
  geminiTopK: number;
  geminiMaxOutputTokens: number;
  googleType: string;
  anthropicToken: number;
  anthropicTopK: number;
  anthropicTopP: number;
  anthropicTemperature: number;
  perplexityTokens: number;
  perplexityTemperature: number;
  perplexityTopP: number;
  perplexityTopK: number;
  search_context_size: 'low' | 'medium' | 'high';
  streamEnabled: boolean;
  streamType: 'chat' | 'form' | 'api_callback';
  apiCallbackUrl: string;
  multimodalEnabled?: boolean;
  reasoning?: boolean;
  isWebEnabled?: boolean;
};

export const systemValueMap: { [key: string]: string } = {
  openAi: 'OpenAI',
  perplexity: 'perplexityAi',
  anthropic: 'anthropicAi',
  groq: 'Groq',
  gemini: 'Gemini',
  replicate: 'openSources',
  evenLabs: 'elevenlabs.io',
  playHt: 'play.ht',
  xai: 'XAi',
};

export const systemDisplayNameMap: { [key: string]: string } = {
  openAi: 'OpenAI',
  perplexityAi: 'Perplexity',
  anthropicAi: 'Anthropic',
  groq: 'Groq',
  gemini: 'Gemini',
  openSources: 'Open Source',
  playHt: 'Play.ht',
  XAi: 'X AI',
};

/**
 * Canonical provider keys stored in `data.aiSystem` (lowercase).
 * These are the only valid values that should be written by the
 * commandTextNode UI. Anything else (e.g. legacy "OpenAI", "anthropicAi")
 * is a leftover from the previous design and must be migrated.
 */
export const CANONICAL_AI_SYSTEMS = [
  'openai',
  'anthropic',
  'gemini',
  'groq',
  'perplexity',
  'xai',
  'opensources',
] as const;

export type CanonicalAiSystem = (typeof CANONICAL_AI_SYSTEMS)[number];

export const CANONICAL_IMAGE_AI_SYSTEMS = [
  'openai',
  'gemini',
  'stabilityai',
  'ideogram',
  'fal',
  'opensources',
] as const;

export type CanonicalImageAiSystem =
  (typeof CANONICAL_IMAGE_AI_SYSTEMS)[number];

export const CANONICAL_AUDIO_AI_SYSTEMS = ['openai', 'gemini', 'groq'] as const;

export type CanonicalAudioAiSystem =
  (typeof CANONICAL_AUDIO_AI_SYSTEMS)[number];

export const CANONICAL_VOICE_AI_SYSTEMS = [
  'elevenlabs',
  'playht',
  'groq',
] as const;

export type CanonicalVoiceAiSystem =
  (typeof CANONICAL_VOICE_AI_SYSTEMS)[number];

export type CanonicalSystem =
  | CanonicalAiSystem
  | CanonicalImageAiSystem
  | CanonicalAudioAiSystem
  | CanonicalVoiceAiSystem;

/**
 * API system name (as returned by the technologies endpoint) →
 * canonical aiSystem key. Used when populating the provider dropdown.
 */
export const apiSystemToCanonical: Record<string, CanonicalSystem> = {
  openAi: 'openai',
  anthropic: 'anthropic',
  gemini: 'gemini',
  groq: 'groq',
  perplexity: 'perplexity',
  xai: 'xai',
  replicate: 'opensources',
  stabilityAi: 'stabilityai',
  ideogram: 'ideogram',
  fal: 'fal',
  evenLabs: 'elevenlabs',
  playHt: 'playht',
};

/** Display name for the provider dropdown, keyed by canonical aiSystem. */
export const canonicalSystemDisplayNameMap: Record<CanonicalAiSystem, string> =
  {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    gemini: 'Gemini',
    groq: 'Groq',
    perplexity: 'Perplexity',
    xai: 'X AI',
    opensources: 'Open Source',
  };

export const canonicalImageSystemDisplayNameMap: Record<
  CanonicalImageAiSystem,
  string
> = {
  openai: 'OpenAI',
  gemini: 'Gemini',
  stabilityai: 'Stability AI',
  ideogram: 'Ideogram',
  fal: 'Flux',
  opensources: 'Open Source',
};

export const canonicalVoiceSystemDisplayNameMap: Record<
  CanonicalVoiceAiSystem,
  string
> = {
  elevenlabs: 'Elevenlabs',
  playht: 'Play.ht',
  groq: 'Groq',
};

export const imageDefaultModelBySystem: Record<CanonicalImageAiSystem, string> =
  {
    stabilityai: 'sd3',
    openai: 'gpt-image-1',
    gemini: 'gemini-2.5-flash-image',
    ideogram: 'ideogram-v3-turbo',
    fal: 'pro',
    opensources: 'kandinsky2',
  };

export const voiceModelBySystem: Record<CanonicalVoiceAiSystem, string> = {
  elevenlabs: 'even-labs-text-to-speech',
  playht: 'play-ht-text-to-voice',
  groq: 'canopylabs/orpheus-v1-english',
};

export const AUDIO_MODEL_OPTIONS: {
  aiSystem: CanonicalAudioAiSystem;
  aiModel: string;
  label: string;
}[] = [
  { aiSystem: 'openai', aiModel: 'whisper-1', label: 'OpenAI' },
  { aiSystem: 'gemini', aiModel: 'gemini-speech-to-text', label: 'Gemini' },
  {
    aiSystem: 'groq',
    aiModel: 'whisper-large-v3',
    label: 'Groq (Whisper Large V3)',
  },
  {
    aiSystem: 'groq',
    aiModel: 'whisper-large-v3-turbo',
    label: 'Groq (Whisper Large V3 Turbo)',
  },
];

const legacyImageSystemToCanonical: Record<string, CanonicalImageAiSystem> = {
  openAi: 'openai',
  gemini: 'gemini',
  stabilityAi: 'stabilityai',
  ideogram: 'ideogram',
  fal: 'fal',
  openSources: 'opensources',
  replicate: 'opensources',
};

const legacyImageModelFieldBySystem: Record<CanonicalImageAiSystem, string> = {
  stabilityai: 'engineId',
  openai: 'model',
  gemini: 'model',
  ideogram: 'ideogramModel',
  fal: 'falModel',
  opensources: 'openSourceModel',
};

const legacyAudioSelectorToCanonical: Record<
  string,
  { aiSystem: CanonicalAudioAiSystem; aiModel: string }
> = {
  openAi: { aiSystem: 'openai', aiModel: 'whisper-1' },
  gemini: { aiSystem: 'gemini', aiModel: 'gemini-speech-to-text' },
  groq: { aiSystem: 'groq', aiModel: 'whisper-large-v3' },
  groq_v3_turbo: { aiSystem: 'groq', aiModel: 'whisper-large-v3-turbo' },
};

const legacyVoiceSystemToCanonical: Record<string, CanonicalVoiceAiSystem> = {
  'elevenlabs.io': 'elevenlabs',
  'play.ht': 'playht',
  Groq: 'groq',
};

const legacyTextSystemToCanonical: Record<string, CanonicalAiSystem> = {
  OpenAI: 'openai',
  openAi: 'openai',
  anthropicAi: 'anthropic',
  anthropic: 'anthropic',
  Gemini: 'gemini',
  gemini: 'gemini',
  Google: 'gemini',
  Groq: 'groq',
  groq: 'groq',
  perplexityAi: 'perplexity',
  perplexity: 'perplexity',
  XAi: 'xai',
  xai: 'xai',
  openSources: 'opensources',
  replicate: 'opensources',
};

const legacyTextModelFieldBySystem: Record<CanonicalAiSystem, string> = {
  openai: 'aiModel',
  anthropic: 'anthropicModel',
  gemini: 'geminiModel',
  groq: 'groqModel',
  perplexity: 'perplexityType',
  xai: 'xaiModel',
  opensources: 'openSourceModelName',
};

const LEGACY_MODEL_IDENTITY_KEYS = [
  'AiSystem',
  'aiModels',
  'voiceSystem',
  'engineId',
  'ideogramModel',
  'falModel',
  'openSourceModel',
  'geminiModels',
  'anthropicModel',
  'geminiModel',
  'groqModel',
  'xaiModel',
  'perplexityType',
  'openSourceModelName',
  'modelSelection',
  'model',
  'voiceModel',
];

export function omitLegacyModelKeys(
  config: Record<string, any> | null | undefined,
): Record<string, any> {
  if (!config) return {};
  return Object.fromEntries(
    Object.entries(config).filter(
      ([key]) => !LEGACY_MODEL_IDENTITY_KEYS.includes(key),
    ),
  );
}

/**
 * Read-only fallback used during the DB transition: derives the canonical
 * { aiSystem, aiModel } pair for an image node, preferring the canonical
 * fields and falling back to the legacy per-provider fields.
 */
export function getImageModelSelection(
  data: Record<string, any> | null | undefined,
): { aiSystem: string; aiModel: string } {
  if (data?.aiSystem && data?.aiModel) {
    return { aiSystem: data.aiSystem, aiModel: data.aiModel };
  }
  const legacySystem = legacyImageSystemToCanonical[data?.aiModels ?? ''];
  if (legacySystem) {
    const legacyModel = data?.[legacyImageModelFieldBySystem[legacySystem]];
    return {
      aiSystem: legacySystem,
      aiModel: legacyModel || imageDefaultModelBySystem[legacySystem],
    };
  }
  return {
    aiSystem: 'stabilityai',
    aiModel: imageDefaultModelBySystem.stabilityai,
  };
}

export function getAudioModelSelection(
  data: Record<string, any> | null | undefined,
): { aiSystem: string; aiModel: string } {
  if (
    data?.aiSystem &&
    data?.aiModel &&
    AUDIO_MODEL_OPTIONS.some(option => option.aiModel === data.aiModel)
  ) {
    return { aiSystem: data.aiSystem, aiModel: data.aiModel };
  }
  const byModelId = AUDIO_MODEL_OPTIONS.find(
    option => option.aiModel === data?.aiModel,
  );
  if (byModelId) {
    return { aiSystem: byModelId.aiSystem, aiModel: byModelId.aiModel };
  }
  const legacySelection = legacyAudioSelectorToCanonical[data?.aiModel ?? ''];
  if (legacySelection) {
    return legacySelection;
  }
  return { aiSystem: 'groq', aiModel: 'whisper-large-v3-turbo' };
}

export function getVoiceModelSelection(
  data: Record<string, any> | null | undefined,
): { aiSystem: string; aiModel: string } {
  const canonical =
    data?.aiSystem &&
    (CANONICAL_VOICE_AI_SYSTEMS as readonly string[]).includes(data.aiSystem)
      ? (data.aiSystem as CanonicalVoiceAiSystem)
      : legacyVoiceSystemToCanonical[data?.voiceSystem ?? ''] || 'elevenlabs';
  return { aiSystem: canonical, aiModel: voiceModelBySystem[canonical] };
}

export function getTextConfigSelection(config: {
  modelType?: string;
  aiConfig?: Record<string, any>;
}): { aiSystem?: string; aiModel?: string } {
  const aiConfig = config?.aiConfig || {};
  if (aiConfig.aiSystem) {
    return {
      aiSystem: aiConfig.aiSystem,
      ...(aiConfig.aiModel ? { aiModel: aiConfig.aiModel } : {}),
    };
  }
  const canonical = legacyTextSystemToCanonical[config?.modelType ?? ''];
  if (!canonical) return {};
  const legacyModel = aiConfig[legacyTextModelFieldBySystem[canonical]];
  return {
    aiSystem: canonical,
    ...(legacyModel ? { aiModel: legacyModel } : {}),
  };
}

export function getImageConfigSelection(config: {
  modelType?: string;
  aiConfig?: Record<string, any>;
}): { aiSystem?: string; aiModel?: string } {
  const aiConfig = config?.aiConfig || {};
  if (aiConfig.aiSystem) {
    return {
      aiSystem: aiConfig.aiSystem,
      ...(aiConfig.aiModel ? { aiModel: aiConfig.aiModel } : {}),
    };
  }
  const canonical = legacyImageSystemToCanonical[config?.modelType ?? ''];
  if (!canonical) return {};
  const legacyModel = aiConfig[legacyImageModelFieldBySystem[canonical]];
  return {
    aiSystem: canonical,
    aiModel: legacyModel || imageDefaultModelBySystem[canonical],
  };
}

export function getVoiceConfigSelection(config: {
  modelType?: string;
  aiConfig?: Record<string, any>;
}): { aiSystem?: string; aiModel?: string } {
  const aiConfig = config?.aiConfig || {};
  if (
    aiConfig.aiSystem &&
    (CANONICAL_VOICE_AI_SYSTEMS as readonly string[]).includes(
      aiConfig.aiSystem,
    )
  ) {
    return {
      aiSystem: aiConfig.aiSystem,
      aiModel: voiceModelBySystem[aiConfig.aiSystem as CanonicalVoiceAiSystem],
    };
  }
  const canonical = legacyVoiceSystemToCanonical[config?.modelType ?? ''];
  if (!canonical) return {};
  return { aiSystem: canonical, aiModel: voiceModelBySystem[canonical] };
}

/**
 * Returns a replacement model id if the currently selected model no longer
 * exists in the available (non-deprecated, active) models for that canonical
 * aiSystem. Returns null when no migration is needed.
 */
export function getDeprecatedModelMigration(
  aiSystem: string,
  aiModel: string,
  technologies: any[],
): string | null {
  if (!aiSystem || !aiModel) return null;

  const apiSystem = Object.entries(apiSystemToCanonical).find(
    ([, canonical]) => canonical === aiSystem,
  )?.[0];
  if (!apiSystem) return null;

  const system = technologies?.find((s: any) => s.system === apiSystem);
  if (!system) return null;

  const availableModels = system.models?.filter(
    (m: any) => m.type === 'Text' && !m.isDeprecated,
  );
  if (!availableModels || availableModels.length === 0) return null;

  if (availableModels.some((m: any) => m.model === aiModel)) return null;

  const defaultModel = availableModels.find((m: any) => m.isDefault);
  return defaultModel ? defaultModel.model : availableModels[0].model;
}

/**
 * Returns the default text model id per canonical aiSystem, e.g.:
 *   { openai: 'gpt-4o', anthropic: 'claude-sonnet-4-5-20250929', ... }
 * Models flagged as deprecated are skipped. If a provider has an
 * `isDefault` model it wins; otherwise the first non-deprecated model is
 * used. Providers with no available models are omitted.
 */
export function getDefaultModelsFromTechnologies(
  technologies: any[],
): Partial<Record<CanonicalAiSystem, string>> {
  const defaults: Partial<Record<CanonicalAiSystem, string>> = {};

  for (const [apiSystem, canonical] of Object.entries(apiSystemToCanonical)) {
    if (!(CANONICAL_AI_SYSTEMS as readonly string[]).includes(canonical)) {
      continue;
    }
    const system = technologies?.find((s: any) => s.system === apiSystem);
    if (!system) continue;

    const validModels = system.models?.filter(
      (m: any) => m.type === 'Text' && !m.isDeprecated,
    );
    if (!validModels || validModels.length === 0) continue;

    const defaultModel =
      validModels.find((m: any) => m.isDefault) || validModels[0];
    if (defaultModel) {
      defaults[canonical as CanonicalAiSystem] = defaultModel.model;
    }
  }

  return defaults;
}

export const commandTextNodeDefaultValues = {
  thinking: '',
  aiFp: '0.5',
  aiModel: 'gpt-4o',
  aiPp: '0.5',
  aiTemperature: '0.5',
  aiTokens: '4000',
  aiEffort: 'low',
  maxAiTokens: '',
  maxOutputTokens: 256,
  temperature: 0.2,
  topP: 0.8,
  topK: 40,
  type: 'text',
  aiSystem: 'openai',
  modelName: '',
  text: '',
  max_length: 500,
  top_p: 1,
  repetition_penalty: 1,
  length_penalty: 1,
  no_repeat_ngram_size: 0,
  seed: -1,
  max_new_tokens: 65,
  system_prompt: '',
  min_new_tokens: 0,
  prompt: '',
  content: '',
  instruction: '',
  persona: '',
  autoToken: true,
  max_tokens: 4096,
  groqTemperature: 0.5,
  groqMaxTokens: 4096,
  groqTopP: 1,
  groqStop: '',
  groqSeed: -1,
  groqName: '',
  geminiTemp: 1,
  geminiTopP: 0.9,
  geminiTopK: 10,
  geminiMaxOutputTokens: 8192,
  googleType: 'Gemini',
  anthropicToken: 8192,
  anthropicTopK: 40,
  anthropicTopP: 1,
  anthropicTemperature: 0.2,
  perplexityTokens: 8192,
  perplexityTemperature: 0.2,
  perplexityTopP: 1,
  perplexityTopK: 40,
  search_context_size: 'low',
  streamEnabled: false,
  streamType: 'chat',
  apiCallbackUrl: '',
  multimodalEnabled: false,
  reasoning: true,
  isWebEnabled: true,
};

export const groqFields = [
  {
    name: 'seed',
    label: 'Seed',
    type: 'number',
    step: '1',
    min: '-1',
    max: '100',
  },

  {
    name: 'groqMaxTokens',
    label: 'Max Tokens',
    type: 'number',
    step: '1',
    min: '1',
    max: '10000',
  },
  {
    name: 'geminiTemp',
    label: 'Temperature',
    type: 'range',
    step: '0.1',
    min: '0',
    max: '1',
  },
  {
    name: 'geminiTopP',
    label: 'Top P',
    type: 'range',
    step: '0.1',
    min: '0',
    max: '1',
  },
];

export const groqModels = [
  'llama3-70b-8192',
  'llama-3.1-8b-instant',
  'gemma2-9b-it',
  'llama-4-maverick-17b-128e-instruct',
  'llama-4-scout-17b-16e-instruct',
  'qwen/qwen3-32b',
  'moonshotai/kimi-k2-instruct-0905',
];

export const googleSystem = [
  { value: 'gemini', name: 'Gemini', disabled: false },
];

export const geminiType = [
  { value: 'text', name: 'Text', disabled: false },
  { value: 'pro', name: 'Text Pro', disabled: false },
];

export const geminiFileds = [
  {
    name: 'geminiMaxOutputTokens',
    label: 'Max Output Tokens',
    type: 'number',
    step: '1',
    min: '1',
    max: '64000',
  },
  {
    name: 'temperature',
    label: 'Temperature (Creativity of LLM response)',
    type: 'range',
    step: '0.1',
    min: '0',
    max: '1',
  },
  {
    name: 'topP',
    label: 'Top-P',
    type: 'range',
    step: '0.1',
    min: '0',
    max: '1',
  },
  {
    name: 'topK',
    label: 'Top-K',
    type: 'range',
    step: '1',
    min: '1',
    max: '40',
  },
];

export const stabilityFields = [
  {
    name: 'width',
    label: 'Width',
    type: 'number',
    step: '128',
    min: '512',
    max: '1024',
  },
  {
    name: 'height',
    label: 'Height',
    type: 'number',
    step: '128',
    min: '512',
    max: '1920',
  },
  {
    name: 'cfg_scale',
    label: 'Cfg Scale',
    type: 'number',
    step: '1',
    min: '1',
    max: '35',
  },
  {
    name: 'steps',
    label: 'Steps',
    type: 'number',
    step: '1',
    min: '10',
    max: '150',
  },
  {
    name: 'image_strength',
    label: 'Image Strength',
    type: 'number',
    step: '0.01',
    min: '0',
    max: '1',
  },
  {
    name: 'promptWeight',
    label: 'Prompt Weight',
    type: 'number',
    step: '0.01',
    min: '0',
    max: '1',
  },
];

export const imageAiModels = [
  {
    value: 'stabilityAi',
    name: 'Stability Ai',
  },
  {
    value: 'falAi',
    name: 'Fal Ai',
  },
  {
    value: 'openAi',
    name: 'Open AI',
  },
  {
    value: 'openSources',
    name: 'Open Sources',
  },
];

export const openSourceModelsImage = [
  {
    value: 'kandinsky2',
    name: 'Kandinsky 2',
  },
];

export const inputFields = [
  {
    name: 'aiTokens',
    label: 'Tokens',
    type: 'number',
    step: '1',
    min: '0',
    max: '8000',
  },
  {
    name: 'aiTemperature',
    label: 'Temperature',
    type: 'range',
    step: '0.1',
    min: '0',
    max: '2.0',
  },
  {
    name: 'aiFp',
    label: 'F-Penalty',
    type: 'range',
    step: '0.1',
    min: '-2.0',
    max: '2.0',
  },
  {
    name: 'aiPp',
    label: 'P-Penalty',
    type: 'range',
    step: '0.1',
    min: '-2.0',
    max: '2.0',
  },
];

export const falcon400bInputs = [
  {
    name: 'max_length',
    label: 'Max Length',
    type: 'number',
    step: '1',
    min: '1',
    max: '700000',
  },
  {
    name: 'temperature',
    label: 'Temperature',
    type: 'range',
    step: '0.01',
    min: '0.01',
    max: '5',
  },
  {
    name: 'top_p',
    label: 'Top P',
    type: 'range',
    step: '0.01',
    min: '0.01',
    max: '1',
  },
  {
    name: 'repetition_penalty',
    label: 'Repetition Penalty',
    type: 'range',
    step: '0.01',
    min: '0.01',
    max: '5',
  },
  {
    name: 'length_penalty',
    label: 'Length Penalty',
    type: 'range',
    step: '0.01',
    min: '0.01',
    max: '5',
  },
  {
    name: 'no_repeat_ngram_size',
    label: 'No Repeat Ngram Size',
    type: 'number',
    step: '0.1',
    min: '0',
    max: '100',
  },
  {
    name: 'seed',
    label: 'Seed',
    type: 'range',
    step: '0.01',
    min: '-1',
    max: '100',
  },
];

export const llama7bInputs = [
  {
    name: 'max_new_tokens',
    label: 'Max New Tokens',
    type: 'number',
    step: '1',
    min: '1',
    max: '999999',
  },
  {
    name: 'min_new_tokens',
    label: 'Min New Tokens',
    type: 'number',
    step: '1',
    min: '-1',
    max: '999999',
  },
  {
    name: 'temperature',
    label: 'Temperature',
    type: 'range',
    step: '0.01',
    min: '0.01',
    max: '5',
  },
  {
    name: 'top_p',
    label: 'Top P',
    type: 'range',
    step: '0.01',
    min: '0.01',
    max: '1',
  },
  {
    name: 'topK',
    label: 'Top-K',
    type: 'range',
    step: '1',
    min: '1',
    max: '100',
  },

  {
    name: 'seed',
    label: 'Seed',
    type: 'range',
    step: '0.01',
    min: '-1',
    max: '100',
  },
];

export const openAiInputs = [
  {
    name: 'aiTemperature',
    label: 'Ai Temperature',
    type: 'range',
    step: '0.1',
    min: '0',
    max: '2',
  },
  {
    name: 'aiFp',
    label: 'Ai Fp',
    type: 'range',
    step: '0.1',
    min: '0',
    max: '2',
  },
  {
    name: 'aiPp',
    label: 'Ai Pp',
    type: 'range',
    step: '0.1',
    min: '0',
    max: '2',
  },
];

export const XAiInputs = [
  {
    name: 'aiTemperature',
    label: 'Ai Temperature',
    type: 'range',
    step: '0.1',
    min: '0',
    max: '2',
  },
  {
    name: 'aiTokens',
    label: 'Tokens',
    type: 'number',
    step: '1',
    min: '0',
    max: '8000',
  },
];

export const additionalOptionsArray = [
  { value: 'anthropicAi', name: 'Anthropic AI', disabled: false },
  { value: 'OpenAI', name: 'OpenAI', disabled: false },
  { value: 'Google', name: 'Google', disabled: false },
  { value: 'Groq', name: 'Groq', disabled: false },
  { value: 'openSources', name: 'Open Source', disabled: false },
  { value: 'perplexityAi', name: 'Perplexity AI', disabled: false },
  { value: 'XAi', name: 'X AI', disabled: false },
];

export const sizes = [
  { width: 1024, height: 1024 },
  { width: 1152, height: 896 },
  { width: 1216, height: 832 },
  { width: 1344, height: 768 },
  { width: 1536, height: 640 },
  { width: 640, height: 1536 },
  { width: 768, height: 1344 },
  { width: 832, height: 1216 },
  { width: 896, height: 1152 },
];

export const options = {
  lighting: [
    { value: 'Sunlight', label: 'Sunlight' },
    { value: 'Rim light', label: 'Rim light' },
    { value: 'Volumetric lighting', label: 'Volumetric lighting' },
    { value: 'Backlight', label: 'Backlight' },
    { value: 'Dimly lit', label: 'Dimly lit' },
    { value: 'Well-lit', label: 'Well-lit' },
    { value: 'Hard light', label: 'Hard light' },
    { value: 'Direct light from...', label: 'Direct light from...' },
    { value: 'Subsurface scattering', label: 'Subsurface scattering' },
    { value: 'Bounce light', label: 'Bounce light' },
    { value: 'Ambient light', label: 'Ambient light' },
  ],
  color: [
    {
      value: 'Monochromatic color scheme',
      label: 'Monochromatic color scheme',
    },
    { value: 'Analogous color scheme', label: 'Analogous color scheme' },
    {
      value: 'Complementary color scheme',
      label: 'Complementary color scheme',
    },
    {
      value: 'Split Complementary color scheme',
      label: 'Split Complementary color scheme',
    },
    {
      value: 'Double Split Complementary color scheme',
      label: 'Double Split Complementary color scheme',
    },
    {
      value: 'Triadic (or triad) color scheme',
      label: 'Triadic (or triad) color scheme',
    },
    { value: 'Square color scheme', label: 'Square color scheme' },
    { value: 'Compound color scheme', label: 'Compound color scheme' },
    { value: 'Rectangle color scheme', label: 'Rectangle color scheme' },
    { value: 'Shades color scheme', label: 'Shades color scheme' },
    { value: '60-30-10 color ratio', label: '60-30-10 color ratio' },
    {
      value: 'Black and white color scheme',
      label: 'Black and white color scheme',
    },
  ],
};
export const viewsArrayOptions = {
  views: [
    { label: 'Bird’s-eye view', value: 'Bird’s-eye view' },
    { label: 'Worm’s-eye view', value: 'Worm’s-eye view' },
    { label: 'Profile view', value: 'Profile view' },
    { label: 'Landscape view', value: 'Landscape view' },
    { label: 'Aerial view', value: 'Aerial view' },
    { label: 'High-angle view', value: 'High-angle view' },
    { label: 'Low-angle view', value: 'Low-angle view' },
    { label: 'Frontal view', value: 'Frontal view' },
    { label: 'Rear view', value: 'Rear view' },
    { label: 'Oblique view', value: 'Oblique view' },
    { label: 'Point of view (POV)', value: 'Point of view (POV)' },
    { label: 'Overhead view', value: 'Overhead view' },
    { label: 'Close-up view', value: 'Close-up view' },
    { label: 'Panoramic view', value: 'Panoramic view' },
    { label: 'Wide-angle view', value: 'Wide-angle view' },
    { label: 'Telephoto view', value: 'Telephoto view' },
    { label: 'Fish-eye view', value: 'Fish-eye view' },
    { label: 'Candid view', value: 'Candid view' },
    { label: '360-degree view', value: '360-degree view' },
    { label: 'Split-view', value: 'Split-view' },
    { label: 'Three-quarter view', value: 'Three-quarter view' },
    { label: 'Interior view', value: 'Interior view' },
    { label: 'Exterior view', value: 'Exterior view' },
    { label: 'Top-down view', value: 'Top-down view' },
    { label: 'Cross-sectional view', value: 'Cross-sectional view' },
    { label: 'Isometric view', value: 'Isometric view' },
    { label: 'Parallel view', value: 'Parallel view' },
    { label: 'Diagonal view', value: 'Diagonal view' },
    { label: 'Circular view', value: 'Circular view' },
    { label: 'Reflection view', value: 'Reflection view' },
  ],
};

export const viewsArray = [
  'AUTO',
  'DDIM',
  'DDPM',
  'K_DPMPP_2M',
  'K_DPMPP_2S_ANCESTRAL',
  'K_DPM_2',
  'K_DPM_2_ANCESTRAL',
  'K_EULER',
  'K_EULER_ANCESTRAL',
  'K_HEUN',
  'K_LMS',
];

export const clipGuidancePresent = [
  'NONE',
  'FAST_BLUE',
  'FAST_GREEN',
  'SIMPLE',
  'SLOW',
  'SLOWER',
  'SLOWEST',
];

export const imageOpenAiModels = [
  {
    name: 'Create Image',
    value: 'createImage',
  },
  {
    name: 'Edit Image',
    value: 'editImage',
  },
  {
    name: 'Image Variation',
    value: 'imageVariation',
  },
];

export const stylePresent = [
  'None',
  '3d-model',
  'analog-film',
  'anime',
  'cinematic',
  'comic-book',
  'digital-art',
  'enhance',
  'fantasy-art',
  'isometric',
  'line-art',
  'low-poly',
  'modeling-compound',
  'neon-punk',
  'origami',
  'photographic',
  'pixel-art',
  'tile-texture',
];
